import React, { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import api from '@/lib/api';
import { io } from 'socket.io-client';

const QuoteMessaging = ({ quoteId, isPaid }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [socket, setSocket] = useState(null);
  const messagesEndRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    fetchMessages();
    
    // Initialize Socket.IO
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const socketConnection = io(backendUrl, {
      transports: ['websocket', 'polling']
    });

    socketConnection.on('connect', () => {
      console.log('Socket connected');
      socketConnection.emit('join_quote', { quote_id: quoteId });
    });

    socketConnection.on('new_message', (message) => {
      setMessages(prev => [...prev, message]);
    });

    socketConnection.on('disconnect', () => {
      console.log('Socket disconnected');
    });

    setSocket(socketConnection);

    return () => {
      if (socketConnection) {
        socketConnection.emit('leave_quote', { quote_id: quoteId });
        socketConnection.disconnect();
      }
    };
  }, [quoteId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await api.get(`/messages/${quoteId}`);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setLoading(true);
    try {
      await api.post('/messages/send', {
        quote_id: quoteId,
        content: newMessage
      });
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-xl font-chivo font-bold text-slate-900 mb-4">
        Messages
      </h3>

      {!isPaid && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-4">
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> Contact information (emails and phone numbers) are hidden in messages until you complete a payment.
          </p>
        </div>
      )}

      <div className="bg-slate-50 rounded-sm p-4 h-96 overflow-y-auto mb-4">
        {messages.length === 0 ? (
          <p className="text-center text-slate-500 py-8">No messages yet</p>
        ) : (
          <div className="space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs px-4 py-2 rounded-sm ${
                    msg.sender_id === user.id
                      ? 'bg-slate-900 text-white'
                      : 'bg-white border border-slate-200'
                  }`}
                >
                  <p className="text-xs font-medium mb-1 opacity-70">
                    {msg.sender_name} ({msg.sender_role})
                  </p>
                  <p className="text-sm">{msg.content}</p>
                  {msg.filtered && (
                    <p className="text-xs mt-1 opacity-60">
                      (Contact info filtered)
                    </p>
                  )}
                  <p className="text-xs mt-1 opacity-50">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <form data-testid="message-form" onSubmit={handleSend} className="flex gap-2">
        <Input
          data-testid="message-input"
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message..."
          disabled={loading}
        />
        <Button
          data-testid="send-message-btn"
          type="submit"
          disabled={loading || !newMessage.trim()}
        >
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

export default QuoteMessaging;
